import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import OrdersPage from './pages/OrdersPage';
import NieuwOrderPage from './pages/NieuwOrderPage';
import OrderDetailPage from './pages/OrderDetailPage';
import ProductFichesPage from './pages/ProductFichesPage';
import ArtikelenPage from './pages/ArtikelenPage';
import WerkvloerPage from './pages/WerkvloerPage';
import InstellingenPage from './pages/InstellingenPage';
import MijnProfielPage from './pages/MijnProfielPage';
import TeBestellenPage from './pages/TeBestellenPage';
import BesteldPage from './pages/BesteldPage';
import PrintPlanningPage from './pages/PrintPlanningPage';

const NAV = [
  { path: '/dashboard',    label: 'Dashboard',    rol: ['planner','admin','werkvloer','backoffice'], kleur: '#185FA5' },
  { path: '/orders',       label: 'Orders',       rol: ['planner','admin','backoffice'],             kleur: '#639922' },
  { path: '/te-bestellen', label: 'Te bestellen', rol: ['planner','admin','backoffice'],             kleur: '#A32D2D' },
  { path: '/besteld',      label: 'Besteld',      rol: ['planner','admin','backoffice'],             kleur: '#854F0B' },
  { path: '/artikelen',    label: 'Artikelen',    rol: ['planner','admin'],                          kleur: '#267A5C' },
  { path: '/productfiches',label: 'Productfiches',rol: ['planner','admin'],                          kleur: '#BA7517' },
  { path: '/productie',    label: 'Productie',    rol: ['planner','admin','werkvloer'],              kleur: '#D85A30' },
  { path: '/instellingen', label: 'Instellingen', rol: ['planner','admin'],                          kleur: '#534AB7' },
  { path: '/mijnprofiel',  label: 'Mijn profiel', rol: ['planner','admin','werkvloer','backoffice'], kleur: '#888'    },
];

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  return isMobile;
}

function Sidebar({ user, onLogout, open, onClose, isMobile }) {
  const loc = useLocation();
  const navItems = NAV.filter(n => n.rol.includes(user?.rol));

  const sidebar = (
    <div style={{
      width: 180, background: '#fff', borderRight: '0.5px solid #ddd',
      display: 'flex', flexDirection: 'column', flexShrink: 0,
      height: '100vh', position: 'fixed', zIndex: 200,
      left: isMobile && !open ? -190 : 0,
      transition: 'left 0.25s ease',
      boxShadow: isMobile && open ? '4px 0 16px rgba(0,0,0,0.15)' : 'none',
    }}>
      <div style={{ padding: '12px 14px', borderBottom: '0.5px solid #ddd' }}>
        <img src="/logo.png" alt="Spoo Linssen" style={{ width: '100%', maxWidth: 148, display: 'block', marginBottom: 6 }} />
        <div style={{ fontSize: 10, color: '#888', fontWeight: 500 }}>{user?.naam}</div>
      </div>
      <div style={{ flex: 1, paddingTop: 6, overflowY: 'auto' }}>
        {navItems.map(n => (
          <Link key={n.path} to={n.path} style={{ textDecoration: 'none' }} onClick={isMobile ? onClose : undefined}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '9px 14px',
              fontSize: 13, color: loc.pathname.startsWith(n.path) ? '#185FA5' : '#555',
              borderLeft: loc.pathname.startsWith(n.path) ? '2px solid #185FA5' : '2px solid transparent',
              background: loc.pathname.startsWith(n.path) ? '#E6F1FB' : 'transparent',
              fontWeight: loc.pathname.startsWith(n.path) ? 500 : 400,
            }}>
              <div style={{ width: 7, height: 7, borderRadius: '50%', background: n.kleur, flexShrink: 0 }} />
              {n.label}
            </div>
          </Link>
        ))}
      </div>
      <div style={{ padding: '10px 14px', borderTop: '0.5px solid #ddd' }}>
        <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>Rol: {user?.rol}</div>
        <button onClick={onLogout} style={{ fontSize: 12, color: '#A32D2D', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>Uitloggen</button>
      </div>
    </div>
  );

  return (
    <>
      {sidebar}
      {/* Overlay op mobiel */}
      {isMobile && open && (
        <div onClick={onClose} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 199,
        }} />
      )}
    </>
  );
}

// Minimale wrapper voor pagina's zonder sidebar (bv. print)
function PrintLayout({ user, children }) {
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function ProtectedLayout({ user, onLogout, children }) {
  const isMobile = useIsMobile();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  if (!user) return <Navigate to="/login" replace />;
  return (
    <div style={{ display: 'flex' }}>
      <Sidebar user={user} onLogout={onLogout} open={sidebarOpen} onClose={() => setSidebarOpen(false)} isMobile={isMobile} />

      <div style={{ marginLeft: isMobile ? 0 : 180, flex: 1, minHeight: '100vh', background: '#f5f5f5' }}>
        {/* Mobiele topbalk */}
        {isMobile && (
          <div style={{
            position: 'sticky', top: 0, zIndex: 100,
            background: '#1F3864', padding: '10px 14px',
            display: 'flex', alignItems: 'center', gap: 12,
            boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
          }}>
            <button onClick={() => setSidebarOpen(true)} style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: '#fff', fontSize: 22, lineHeight: 1, padding: '2px 4px',
            }}>☰</button>
            <img src="/logo.png" alt="Spoo Linssen" style={{ height: 28, display: 'block' }} />
          </div>
        )}
        {children}
      </div>
    </div>
  );
}

export default function App() {
  const [user, setUser] = useState(() => {
    const u = localStorage.getItem('user');
    return u ? JSON.parse(u) : null;
  });

  const handleLogin = (userData, token) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const handleUserUpdate = (userData) => {
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={user ? <Navigate to="/dashboard" replace /> : <LoginPage onLogin={handleLogin} />} />
        <Route path="/" element={<Navigate to={user ? "/dashboard" : "/login"} replace />} />
        <Route path="/dashboard"    element={<ProtectedLayout user={user} onLogout={handleLogout}><DashboardPage user={user} /></ProtectedLayout>} />
        <Route path="/orders"       element={<ProtectedLayout user={user} onLogout={handleLogout}><OrdersPage user={user} /></ProtectedLayout>} />
        <Route path="/orders/nieuw" element={<ProtectedLayout user={user} onLogout={handleLogout}><NieuwOrderPage /></ProtectedLayout>} />
        <Route path="/orders/:id"   element={<ProtectedLayout user={user} onLogout={handleLogout}><OrderDetailPage user={user} /></ProtectedLayout>} />
        <Route path="/artikelen"    element={<ProtectedLayout user={user} onLogout={handleLogout}><ArtikelenPage user={user} /></ProtectedLayout>} />
        <Route path="/productfiches"element={<ProtectedLayout user={user} onLogout={handleLogout}><ProductFichesPage user={user} /></ProtectedLayout>} />
        <Route path="/productie"    element={<ProtectedLayout user={user} onLogout={handleLogout}><WerkvloerPage user={user} onUserUpdate={handleUserUpdate} /></ProtectedLayout>} />
        {/* oude URL blijft werken */}
        <Route path="/werkvloer"    element={<Navigate to="/productie" replace />} />
        <Route path="/instellingen" element={<ProtectedLayout user={user} onLogout={handleLogout}><InstellingenPage user={user} /></ProtectedLayout>} />
        <Route path="/te-bestellen" element={<ProtectedLayout user={user} onLogout={handleLogout}><TeBestellenPage /></ProtectedLayout>} />
        <Route path="/besteld"      element={<ProtectedLayout user={user} onLogout={handleLogout}><BesteldPage /></ProtectedLayout>} />
        <Route path="/mijnprofiel"  element={<ProtectedLayout user={user} onLogout={handleLogout}><MijnProfielPage user={user} onUserUpdate={handleUserUpdate} /></ProtectedLayout>} />
        <Route path="/print-planning" element={<PrintLayout user={user}><PrintPlanningPage user={user} /></PrintLayout>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
